import { useState, useMemo, useLayoutEffect } from 'react';

import { Button } from "@/components/ui/button";
import { Table_NEW } from '@/components/custom/organisms/Table';

import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { AddProjectModal } from './AddProjectModal';
import { SelectProjectModal } from './SelectProjectModal';

import { Info } from 'lucide-react';

export type Project = {
    id: string;
    name: string;
}

type Props = {
    onNext: (data: Project[]) => void;
    onClose: () => void;
    defaultData: Project[] | [];
}

const Step1: React.FC<Props> = ({
    onNext,
    onClose,
    defaultData,
}) => {
    const [selectedProjects, setSelectedProjects] = useState([] as Project[]);
    useLayoutEffect(() => {
        setSelectedProjects(defaultData);
    }, [])

    const onAddProjectHandler = (project: Project) => {
        setSelectedProjects(prevProjects => [...prevProjects, project]);
    }

    const onRemoveProjectHandler = (id: string) => {
        setSelectedProjects(prevProjects => prevProjects.filter(project => String(project.id) !== String(id)));
    }

    const onNextHandler = () => {
        onNext(selectedProjects);
    }

    const tableData = useMemo(() => selectedProjects, [selectedProjects])
    const columnHelper = createColumnHelper<Project>();
    const defaultColumns = [
        columnHelper.display({
            id: 'question',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-file-person-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Project Name</span>
                </div>
            ),
            cell: (params) => (
                <div className="flex">
                    {params.row.original.name}
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-gear-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Actions</span>
                </div>
            ),
            cell: (params) => (
                <TooltipProvider>
                    <div className="flex justify-center w-full gap-1" >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="destructive"
                                    size='action'
                                    className={`text-nowrap flex text-sm`}
                                    onClick={() => onRemoveProjectHandler(params.row.original.id)}
                                >
                                    <i className="text-md bi bi-trash"></i>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Delete</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            ),
            size: 100,
            maxSize: 100,
        }),
    ]
    const table = useReactTable({
        columns: defaultColumns,
        data: tableData ?? [],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <div className="my-2 flex items-center text-blue-600">
                <Info className="flex-shrink-0 me-1" size="16"/>
                <p className="text-xs">Select which existing projects you would like to connect this CRM to or create a new project to connect this CRM to.</p>
            </div>

            <div className="w-full h-full max-h-80 overflow-scroll-y wissp-scroll-box pe-2 grow">
                <Table_NEW 
                    table={table} 
                />
            </div>
            <div className="flex justify-end space-x-2">
                <AddProjectModal 
                    onCreateProject={onAddProjectHandler}
                />
                <SelectProjectModal
                    selectedProjects={selectedProjects}
                    onAddProject={onAddProjectHandler}
                />
            </div>

            <div className="pt-3 mt-3 border-t w-full flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>

                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                    <Button disabled={selectedProjects.length === 0} onClick={onNextHandler}>Next</Button>
                </div>
            </div>
        </>
    )
}

export { Step1 };
